import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Spin } from "../../../design-system";
import { safeTextGET } from "../../../common/api/SafeClient";
import classes from "./DemoReports.module.less";

interface DemoReport {
    id: string;
    name: string;
    type: string;
    description: string;
    generatedAt: string;
}

const DEMO_REPORTS: DemoReport[] = [
    {
        id: "forecast",
        name: "Category Spend and Opportunity Forecast",
        type: "Forecast",
        description:
            "Forward-looking view of category spend with projected savings opportunities and consolidation levers.",
        generatedAt: "May 14, 2026",
    },
    {
        id: "category-analysis",
        name: "Professional Services Consolidation",
        type: "Category Analysis",
        description:
            "Deep-dive analysis of category spend, supplier overlap, and consolidation opportunities.",
        generatedAt: "May 9, 2026",
    },
    {
        id: "portfolio-commitment-scan",
        name: "Sell-Side Commitment and Price Realization Scan",
        type: "Portfolio Scan",
        description:
            "Full-portfolio scan of active sell-side agreements scoring commitments, price realization, and exposure across 14 clause families.",
        generatedAt: "July 7, 2026",
    },
    {
        id: "sell-side-deal-analysis",
        name: "Industrial Coatings Supply Agreement",
        type: "Sell-Side Analysis",
        description:
            "Single-agreement sell-side analysis of contract exposure, renewal levers, and renegotiation strategy for a fixed-price supply agreement.",
        generatedAt: "July 7, 2026",
    },
];

// Fetched and rendered via srcDoc rather than pointing the iframe at the API
// host, same pattern as FlexibleOutput: the API responds with frame-ancestors
// 'self', so in prod the SPA origin is not allowed to frame api.infinityloop.ai
// directly.
const getDemoReportContent = (
    reportId: string,
    abort?: { signal: AbortSignal }
): Promise<string> =>
    safeTextGET(`/api/project/demo-report/${encodeURIComponent(reportId)}/content`, abort);

export const DemoReportsListView: React.FC = () => {
    const navigate = useNavigate();

    return (
        <Card bordered={false}>
            <div className={classes.header}>
                <div className={classes.title}>Reports</div>
            </div>
            <div className={classes.list}>
                {DEMO_REPORTS.map((report) => (
                    <div
                        key={report.id}
                        className={classes.card}
                        onClick={() => navigate(`/superadmin/demo/reports/${report.id}`)}
                    >
                        <div className={classes.cardType}>{report.type}</div>
                        <div className={classes.cardName}>{report.name}</div>
                        <div className={classes.cardDescription}>{report.description}</div>
                        <div className={classes.cardMeta}>Generated {report.generatedAt}</div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export const DemoReportDetailView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const report = DEMO_REPORTS.find((r) => r.id === id);

    const [reportHtml, setReportHtml] = useState<string | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (!report) {
            return;
        }
        const controller = new AbortController();
        setReportHtml(null);
        setLoadFailed(false);
        getDemoReportContent(report.id, { signal: controller.signal })
            .then(setReportHtml)
            .catch(() => {
                if (!controller.signal.aborted) {
                    setLoadFailed(true);
                }
            });
        return () => controller.abort();
    }, [report?.id]);

    if (!report) {
        return (
            <Card>
                <p>Report not found.</p>
                <Button onClick={() => navigate("/superadmin/demo/reports")}>
                    Back to Reports
                </Button>
            </Card>
        );
    }

    return (
        <div className={classes.detail}>
            <div className={classes.detailHeader}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate("/superadmin/demo/reports")}
                >
                    Back to Reports
                </Button>
                <div className={classes.detailTitle}>
                    <span className={classes.detailType}>{report.type}</span>
                    <span className={classes.detailName}>{report.name}</span>
                </div>
            </div>
            {loadFailed ? (
                <div className={classes.frameStatus}>
                    <Alert
                        type="error"
                        message="The report failed to load. Refresh the page to try again."
                    />
                </div>
            ) : reportHtml === null ? (
                <div className={classes.frameStatus}>
                    <Spin size="large" />
                </div>
            ) : (
                <iframe
                    className={classes.frame}
                    srcDoc={reportHtml}
                    title={report.name}
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
            )}
        </div>
    );
};
